import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";

import Checkbox from "@material-ui/core/Checkbox";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { makeStyles } from "@material-ui/core/styles";
import MailOutline from "@material-ui/icons/MailOutline";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardIcon from "components/Card/CardIcon.js";
import CardBody from "components/Card/CardBody.js";

import styles from "assets/jss/material-dashboard-pro-react/views/formsStyle.js";
import {
  GetIDRTopUpMethods,
  GetSGDTopUpMethods,
  GetTopUpMethods
} from "enums/TopUpMethod";
import { creditMonitoringApi, masterApi } from "functions/apis";
import { token } from "functions/Helper";

const useStyles = makeStyles(styles);

export default function Create(props) {
  const source = useRef(new AbortController());

  useEffect(() => {
    return () => source.current.abort();
  }, []);

  const [currencyList, setCurrencyList] = React.useState([]);
  const [state, setState] = React.useState({
    method: null,
    currencyID: null,
    hasMinimumLimit: false,
    minimumLimit: 0,
    hasMaximumLimit: false,
    maximumLimit: 0
  });
  const [redirect, setRedirect] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);

  useEffect(() => {
    if (
      token["master.currencies.read"] === "allowed" ||
      token.is_admin === "True"
    )
      masterApi
        .get("/Currencies", {
          signal: source.current.signal,
          params: {
            pagination: {
              pageIndex: 0,
              pageSize: 0
            },
            filter: {
              searchString: null,
              publicationStatus: 1
            }
          }
        })
        .then(resp => setCurrencyList(resp.records))
        .catch(error => console.log(error));
  }, []);

  const selectedCurrency = useMemo(
    () => currencyList.find(currency => currency.id === state.currencyID),
    [currencyList, state.currencyID]
  );

  const methodOptions = useMemo(() => {
    if (selectedCurrency?.code === "IDR") return GetIDRTopUpMethods();
    if (selectedCurrency?.code === "SGD") return GetSGDTopUpMethods();
    return GetTopUpMethods();
  }, [selectedCurrency]);

  useEffect(() => {
    if (
      state.method !== null &&
      !methodOptions.some(option => option.key === state.method)
    )
      setState(prev => ({
        ...prev,
        method: null
      }));
  }, [methodOptions, state.method]);

  const handleCurrencyIDChanged = e => {
    const value = e?.target?.value;
    setState(prev => ({
      ...prev,
      currencyID: value === "" ? null : value
    }));
  };

  const handleMethodChanged = e => {
    const value = e?.target?.value;
    setState(prev => ({
      ...prev,
      method: value === "" ? null : parseInt(value)
    }));
  };

  const handleHasMinimumLimitChanged = e => {
    const checked = e?.target?.checked ?? false;
    setState(prev => ({
      ...prev,
      hasMinimumLimit: checked
    }));
  };

  const handleMinimumLimitChanged = e => {
    const value = e?.target?.value;
    setState(prev => ({
      ...prev,
      minimumLimit: value === "" || value === undefined ? 0 : value
    }));
  };

  const handleHasMaximumLimitChanged = e => {
    const checked = e?.target?.checked ?? false;
    setState(prev => ({
      ...prev,
      hasMaximumLimit: checked
    }));
  };

  const handleMaximumLimitChanged = e => {
    const value = e?.target?.value;
    setState(prev => ({
      ...prev,
      maximumLimit: value === "" || value === undefined ? 0 : value
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    setDisabled(true);

    creditMonitoringApi
      .post(
        "/TopUpLimitConfigurations",
        {
          method: state.method,
          currencyID: state.currencyID,
          hasMinimumLimit: state.hasMinimumLimit,
          minimumLimit: parseFloat(
            state.minimumLimit === "" ? 0 : state.minimumLimit
          ),
          hasMaximumLimit: state.hasMaximumLimit,
          maximumLimit: parseFloat(
            state.maximumLimit === "" ? 0 : state.maximumLimit
          )
        },
        {
          signal: source.current.signal
        }
      )
      .then(() => setRedirect(true))
      .catch(error => {
        console.log(error);
        setDisabled(false);
      });
  };

  const classes = useStyles();

  return redirect ? (
    <Redirect to="/admin/TopUpLimitConfigurations/List" />
  ) : (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <MailOutline />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              Create Top Up Limit Configuration
            </h4>
          </CardHeader>
          <CardBody>
            <Button onClick={() => props.history.goBack()} value="Back">
              BACK
            </Button>
            <form onSubmit={handleSubmit}>
              <GridContainer>
                <GridItem xs={12} sm={6} md={6}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel
                      htmlFor="selCurrency"
                      className={classes.selectLabel}
                    >
                      Currency
                    </InputLabel>
                    <Select
                      MenuProps={{
                        className: classes.selectMenu
                      }}
                      classes={{
                        select: classes.select
                      }}
                      value={state.currencyID === null ? "" : state.currencyID}
                      onChange={handleCurrencyIDChanged}
                    >
                      {currencyList.map(currency => (
                        <MenuItem
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected
                          }}
                          value={currency.id}
                          key={currency.id}
                        >
                          <b>{currency.code}</b>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                  <FormControl fullWidth className={classes.selectFormControl}>
                    <InputLabel
                      htmlFor="selMethod"
                      className={classes.selectLabel}
                    >
                      Method
                    </InputLabel>
                    <Select
                      MenuProps={{
                        className: classes.selectMenu
                      }}
                      classes={{
                        select: classes.select
                      }}
                      value={state.method === null ? "" : state.method}
                      onChange={handleMethodChanged}
                    >
                      {methodOptions.map(method => (
                        <MenuItem
                          classes={{
                            root: classes.selectMenuItem,
                            selected: classes.selectMenuItemSelected
                          }}
                          value={method.key}
                          key={method.key}
                        >
                          <b>{method.description}</b>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12} sm={6} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={state.hasMinimumLimit}
                        onChange={handleHasMinimumLimitChanged}
                      />
                    }
                    label="Has Minimum Limit"
                  />
                  <CustomInput
                    labelText="Minimum Limit"
                    formControlProps={{
                      fullWidth: true
                    }}
                    inputProps={{
                      type: "number",
                      disabled: !state.hasMinimumLimit,
                      onChange: handleMinimumLimitChanged,
                      value: state.minimumLimit
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={state.hasMaximumLimit}
                        onChange={handleHasMaximumLimitChanged}
                      />
                    }
                    label="Has Maximum Limit"
                  />
                  <CustomInput
                    labelText="Maximum Limit"
                    formControlProps={{
                      fullWidth: true
                    }}
                    inputProps={{
                      type: "number",
                      disabled: !state.hasMaximumLimit,
                      onChange: handleMaximumLimitChanged,
                      value: state.maximumLimit
                    }}
                  />
                </GridItem>
              </GridContainer>
              <Button disabled={disabled} color="rose" type="submit">
                Submit
              </Button>
            </form>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}

Create.propTypes = {
  history: PropTypes.object
};
